import clsx from "clsx";
import { FC, useMemo } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { MsgExec, priceFormatter } from "rujira.js";
import {
  Button,
  Decimal,
  /* Decimal, */
  i18n,
  Icons,
  useGlobalModalContext,
  Warning,
} from "rujira.ui";
import exclamation from "rujira.ui/src/assets/images/exclamation.gif";
import { Info } from "rujira.ui/src/components/icons/Icons";
import { usePreloadedBalance } from "../../common/components/Balance";
import { useNotificationModal } from "../../common/components/Notifications";
import {
  MsgProvider,
  TxBalanceAccountButton,
  TxButton,
} from "../../common/components/TxButton";
import {} from "../../portfolio/utils";
import { Side } from "../types";
import { calculateReturnAmount } from "../utils";
import {
  SubmitMarketFragment$data,
  SubmitMarketFragment$key,
} from "./__generated__/SubmitMarketFragment.graphql";
import { useAmount } from "./Context";
import { InputQuantity } from "./InputQuantity";

const fragment = graphql`
  fragment SubmitMarketFragment on FinPair {
    address
    tick
    assetBase {
      asset
      type
      chain
      metadata {
        decimals
        symbol
      }
      variants {
        native {
          denom
        }
      }
    }
    assetQuote {
      asset
      type
      chain
      metadata {
        decimals
        symbol
      }
      variants {
        native {
          denom
        }
      }
    }
    book {
      bids {
        total
        price
        virtualValue
        value
      }
      center
      asks {
        total
        price
        virtualValue
        value
      }
    }
    feeMaker
    feeTaker
    deploymentStatus
  }
`;

interface Props {
  k: SubmitMarketFragment$key;
  side: Side;
}

export const SubmitMarket: FC<Props> = ({ k, side }) => {
  const [amount, setAmount] = useAmount();
  const d = useFragment(fragment, k);
  const { request } = useNotificationModal();

  const book = side === Side.Quote ? d.book.asks : d.book.bids;

  const totalReturn = book ? calculateReturnAmount([...book], amount) : 0n;

  const fee = (BigInt(d.feeTaker || "0") * totalReturn) / 10n ** 12n;
  const returned = totalReturn - fee;
  const mid = d.book?.center ? BigInt(d.book.center) : null;

  const rate =
    side === Side.Base
      ? amount
        ? (totalReturn * 10n ** 12n) / amount
        : 0n
      : totalReturn
        ? (amount * 10n ** 12n) / totalReturn
        : 0n;

  const impact = mid && rate ? ((rate - mid) * 10n ** 12n) / mid : 0n;

  const { account } = usePreloadedBalance();
  const msg = useMemo(() => {
    if (!account) return null;
    if (!amount) return null;

    return new MsgExec(
      { address: account.address, network: "THOR" },
      account.asset,
      amount,
      d.address,
      {
        swap: { min_return: ((returned * 99n) / 100n).toString() },
      }
    );
  }, [side, amount, d.address]);

  const marketWarning = amount > 0n && returned === 0n;
  const marketDanger = impact > 50000000000n || impact < -50000000000n;

  const buttonLabel =
    d.deploymentStatus === "LIVE"
      ? `${side === Side.Quote ? "Buy" : "Sell"} ${d.assetBase ? d.assetBase.metadata.symbol : ""}`
      : "Preview Only";

  const [symbolIn, symbolOut] =
    side === Side.Base
      ? [d.assetBase.metadata.symbol, d.assetQuote.metadata.symbol]
      : [d.assetQuote.metadata.symbol, d.assetBase.metadata.symbol];

  const { showModal, hideModal } = useGlobalModalContext();

  const confirm = (impact: bigint) => {
    if (!msg) return;
    showModal({
      //title: "Confirm",
      backgroundClose: true,
      children: (
        <MsgProvider msg={msg}>
          <ConfirmModal
            impact={impact}
            side={side}
            label={buttonLabel}
            onSuccess={request}
            cancel={hideModal}
          />
        </MsgProvider>
      ),
    });
  };

  return (
    <MsgProvider msg={msg}>
      <InputQuantity amount={amount} setAmount={setAmount}>
        <i18n.label>
          {i18n.t("Qty")}. <small>({symbolIn})</small>
        </i18n.label>
      </InputQuantity>

      <div className="flex dir-c gap-y-1 mt-2 mb-4 fs-12 color-grey condensed">
        <div className="flex ai-c grow trade__quote">
          <div className="flex dir-c grow gap-y-1">
            <div className="flex">
              <i18n.div className="condensed color-white grow fs-14">
                Estimated Price
              </i18n.div>
              <Decimal
                amount={BigInt(rate)}
                decimals={12}
                className="ml-a color-white fs-14"
                symbol={d.assetQuote.metadata.symbol}
                symbolClassName="color-grey condensed symbol inline-block text-left"
              />
            </div>
            <div className="flex">
              <i18n.div className="condensed color-primary1 grow fs-14">
                Estimated Return
              </i18n.div>
              <Decimal
                amount={BigInt(returned)}
                className="ml-a color-white fs-14"
                symbol={symbolOut}
                symbolClassName="color-grey condensed symbol inline-block text-left"
              />
            </div>
          </div>
        </div>

        {marketWarning ? (
          <Warning
            className="mt-1 warning--sm warning--orange"
            msg="Cannot place a Market order. Please use an Optimized order instead."
          />
        ) : (
          <>
            <div
              className="flex"
              data-tooltip-id="global-tip"
              data-tooltip-float={true}
              data-tooltip-content={`${(Number(d.feeTaker) / 1e10).toLocaleDecimal(3)}% taker fee`}>
              <div className="col">{i18n.t("Estimated Fee")}*</div>
              <div className="col text-right color-white">
                {priceFormatter(fee * 10000n)}
                <span className="color-grey condensed symbol inline-block text-left ml-0.5">
                  {symbolOut}
                </span>
              </div>
            </div>
            <div className="flex">
              <i18n.div className="col">Estimated Price Impact</i18n.div>
              <div
                className={clsx({
                  "col flex ai-c jc-e": true,
                  "color-white": !marketDanger,
                  "color-red": marketDanger,
                })}>
                {marketDanger && (
                  <>
                    {side === Side.Base ? (
                      <Icons.TrendDown className="inline-block w-2 h-2 mr-0.5" />
                    ) : (
                      <Icons.TrendUp className="inline-block w-2 h-2 mr-0.5" />
                    )}
                  </>
                )}
                {(Math.abs(Number(impact)) / 1e10).toLocaleDecimal(2)}%
              </div>
            </div>
          </>
        )}
      </div>

      <i18n.p
        data-tooltip-html={`
          <div class="w-25">
            ${i18n.t("Instantly buy or sell a given quantity. The execution price depends on the liquidity available in the orderbook.")}<div>`}
        data-tooltip-id="global-tip"
        className="color-grey fs-12 text-center flex ai-c jc-c fw-500">
        What are market orders?
        <Info className="w-2 h-2 ml-0.5" />
      </i18n.p>
      <div>
        <TxBalanceAccountButton
          hideSimulation
          Component={(props) =>
            marketDanger ? (
              <Button
                className="button--red w-full"
                label={buttonLabel}
                onClick={() => confirm(impact)}>
                <img src={exclamation} alt="" className="filter-white big" />
              </Button>
            ) : (
              <TxButton {...props} />
            )
          }
          onSuccess={request}
          disabled={marketWarning || d.deploymentStatus !== "LIVE"}
          required={{
            asset: side === Side.Base ? d.assetBase : d.assetQuote,
            amount,
          }}
          label={buttonLabel}
          className={clsx({
            "w-full": true,
            "button--blue": side == Side.Quote,
            "button--red": side == Side.Base,
          })}
        />
      </div>
    </MsgProvider>
  );
};

const ConfirmModal: FC<{
  side: Side;
  label: string;
  impact: bigint;
  onSuccess: () => void;
  cancel: () => void;
}> = ({ side, label, onSuccess, cancel, impact }) => {
  return (
    <>
      <div className="flex ai-c jc-c">
        <img
          src={exclamation}
          alt=""
          className="filter-orange block w-6 h-a no-shrink"
        />
        <span className="color-orange fs-14 fw-600 mx-1">
          This order will cause{" "}
          <span className="color-white">
            {(Math.abs(Number(impact)) / 1e10).toLocaleDecimal(2)}%
          </span>{" "}
          of price impact. Proceed with caution.
        </span>
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            onSuccess={onSuccess}
            label={label}
            className={clsx({
              "button ml-1": true,
              "button--blue": side == Side.Quote,
              "button--red": side == Side.Base,
            })}
          />
        </div>
      </div>
    </>
  );
};

/**
 * Predicts the number of blocks that arbitrage will take, considering only atomic
 * and base layer liquidity
 */
export const calculateDuration = (
  offer: bigint,
  book?: SubmitMarketFragment$data["book"]["asks"]
): bigint | null => {
  if (!book) return null;
  const atomic = book.reduce((a, v) => a + BigInt(v.value), 0n);
  const remaining = atomic >= offer ? 0n : offer - atomic;
  if (!remaining) return 0n;
  const virtualValue = book.reduce((a, v) => a + BigInt(v.virtualValue), 0n);
  if (!virtualValue) return -1n;
  return 1n + remaining / virtualValue;
};
